import { useState } from "react";
import {
  Alert,
  Button,
  ButtonVariant,
  Text,
  TextField,
  Toggle,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import { Check, Trash2 } from "lucide-react";
import { EligibilityRule, PlanStep } from "../types";
import { displayStepType, fullStepType, windowSyncStepAllowsFullSync } from "../helpers";
import ConductorDrawer from "./ConductorDrawer";
import EligibilityBuilder from "./EligibilityBuilder";
import MetadataEditor from "./MetadataEditor";
import InlinePlanCreate from "./InlinePlanCreate";
import { FieldLabel, Section, Select } from "./ConductorFormFields";

interface Option {
  id: string;
  name: string;
}

interface Props {
  step: PlanStep | null; // null = adding a new step
  suggestedPriority: number;
  integrationId: string;
  category?: string;
  blueprintOptions: Option[];
  planOptions: Option[]; // integration plans, excluding this one
  onClose: () => void;
  onApply: (step: PlanStep) => void; // merge into the pending draft (not saved yet)
  onRemove?: () => void; // remove from the pending draft (editing only)
  onPlanCreated: () => void; // a nested plan was created inline; reload plan options
}

interface StepDraft {
  id: string | null;
  type: string; // full conductor enum, e.g. PLAN_STEP_TYPE_BLUEPRINT
  blueprintId: string;
  nestedPlanId: string;
  priority: number;
  enabled: boolean;
  eligibility: EligibilityRule[];
  metadata: Record<string, unknown>;
  isWindowSyncStep: boolean;
}

const draftFromStep = (step: PlanStep | null, suggestedPriority: number): StepDraft => ({
  id: step?.id ?? null,
  type: step?.type ?? fullStepType("BLUEPRINT"),
  blueprintId: step?.blueprint_id ?? "",
  nestedPlanId: step?.nested_plan_id ?? "",
  priority: step?.priority ?? suggestedPriority,
  enabled: step?.enabled ?? true,
  eligibility: step?.eligibility ?? [],
  metadata: step?.metadata ?? {},
  isWindowSyncStep: step?.is_window_sync_step ?? false,
});

const StepEditorDrawer = ({
  step,
  suggestedPriority,
  integrationId,
  category,
  blueprintOptions,
  planOptions,
  onClose,
  onApply,
  onRemove,
  onPlanCreated,
}: Props) => {
  const [draft, setDraft] = useState<StepDraft>(() => draftFromStep(step, suggestedPriority));
  const update = (patch: Partial<StepDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const isNew = !onRemove;
  const isBlueprint = displayStepType(draft.type) === "BLUEPRINT";
  const targetMissing = isBlueprint ? !draft.blueprintId : !draft.nestedPlanId;
  const windowSyncBlocked =
    draft.isWindowSyncStep && !windowSyncStepAllowsFullSync(draft.eligibility);

  // If the current target isn't in the option list (e.g. unknown blueprint id),
  // still show it so the value is visible and not silently dropped.
  const blueprintHasOption = blueprintOptions.some((o) => o.id === draft.blueprintId);
  const planHasOption = planOptions.some((o) => o.id === draft.nestedPlanId);

  // The blueprint picker is searchable (by name or id). Include the current
  // value as an option if it isn't in the resolved list, so it stays selected.
  const blueprintPickerOptions =
    blueprintHasOption || !draft.blueprintId
      ? blueprintOptions
      : [{ id: draft.blueprintId, name: draft.blueprintId }, ...blueprintOptions];
  const selectedBlueprint = blueprintPickerOptions.find((o) => o.id === draft.blueprintId) ?? null;

  const apply = () => {
    onApply({
      id: draft.id,
      plan_id: null,
      type: draft.type,
      blueprint_id: isBlueprint ? draft.blueprintId || null : null,
      nested_plan_id: isBlueprint ? null : draft.nestedPlanId || null,
      nested_plan: null,
      eligibility: draft.eligibility,
      priority: draft.priority,
      enabled: draft.enabled,
      metadata: draft.metadata,
      // Valid only for blueprint steps; a nested-plan step can never be one.
      is_window_sync_step: isBlueprint && draft.isWindowSyncStep,
    });
  };

  return (
    <ConductorDrawer
      title={isNew ? "New step" : "Edit step"}
      subtitle={draft.id || undefined}
      onClose={onClose}
      footer={
        <>
          {onRemove ? (
            <Button
              size="sm"
              variant={ButtonVariant.DangerText}
              leftIcon={<Trash2 size={14} />}
              onClick={onRemove}
            >
              Remove step
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant={ButtonVariant.SecondaryGray} onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={ButtonVariant.PrimaryGray}
              leftIcon={<Check size={14} />}
              onClick={apply}
              disabled={targetMissing || windowSyncBlocked}
            >
              {isNew ? "Add step" : "Apply"}
            </Button>
          </div>
        </>
      }
    >
      <Section title="Step">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <FieldLabel>Kind</FieldLabel>
            <div className="inline-flex rounded-md border border-gray-20 p-0.5">
              {(["BLUEPRINT", "PLAN"] as const).map((kind) => {
                const active = displayStepType(draft.type) === kind;
                return (
                  <button
                    key={kind}
                    onClick={() =>
                      update({
                        type: fullStepType(kind),
                        // Window sync is valid only for blueprint steps.
                        ...(kind === "PLAN" ? { isWindowSyncStep: false } : {}),
                      })
                    }
                    className={`rounded px-3 py-1 text-sm font-medium ${
                      active ? "bg-gray-10 text-gray-90" : "text-gray-60 hover:text-gray-90"
                    }`}
                  >
                    {kind === "PLAN" ? "PLAN (nested)" : "BLUEPRINT"}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Toggle checked={draft.enabled} onChange={(value) => update({ enabled: value })} />
            <Text variant="sm" className="text-gray-60">
              {draft.enabled ? "Enabled" : "Disabled"}
            </Text>
          </div>
        </div>

        {isBlueprint && (
          <div className="mb-4 flex items-center justify-between gap-4 rounded-md border border-gray-10 bg-gray-0 p-3">
            <div>
              <Text variant="md" className="text-gray-90">
                Window sync step
              </Text>
              <Text variant="sm" className="mt-0.5 block text-gray-50">
                Runs only on a full sync (or as a catch-up). Eligibility can&apos;t be
                SUBSEQUENT-only.
              </Text>
            </div>
            <Toggle
              checked={draft.isWindowSyncStep}
              onChange={(value) => update({ isWindowSyncStep: value })}
            />
          </div>
        )}

        {isBlueprint ? (
          <div>
            <FieldLabel>Blueprint</FieldLabel>
            <Typeahead
              options={blueprintPickerOptions}
              value={selectedBlueprint}
              onChange={(_: unknown, option: Option | null) =>
                update({ blueprintId: option?.id ?? "" })
              }
              getOptionLabel={(option: Option) =>
                option.name && option.name !== option.id
                  ? `${option.name} (${option.id})`
                  : option.id
              }
              filterOptions={(options: Option[], state: { inputValue: string }) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter(
                  (o) => o.name.toLowerCase().includes(query) || o.id.toLowerCase().includes(query)
                );
              }}
              renderOption={(option: Option) => (
                <div className="flex flex-col">
                  <Text variant="sm" className="font-medium text-gray-90">
                    {option.name}
                  </Text>
                  <Text variant="sm" className="block font-mono text-gray-60">
                    {option.id}
                  </Text>
                </div>
              )}
              placeholder="Search blueprints by name or id"
            />
          </div>
        ) : (
          <div>
            <FieldLabel>Nested plan</FieldLabel>
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <Select
                  value={draft.nestedPlanId}
                  onChange={(value) => update({ nestedPlanId: value })}
                >
                  <option value="">Select a plan…</option>
                  {!planHasOption && draft.nestedPlanId && (
                    <option value={draft.nestedPlanId}>{draft.nestedPlanId}</option>
                  )}
                  {planOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              </div>
              <InlinePlanCreate
                integrationId={integrationId}
                category={category}
                onCreated={(planId) => {
                  update({ nestedPlanId: planId });
                  onPlanCreated();
                }}
              />
            </div>
            <Text variant="sm" className="mt-1 block text-gray-50">
              Spawns a child sync-plan execution. Must share the plan&apos;s integration.
            </Text>
          </div>
        )}

        <div className="mt-4 w-28">
          <FieldLabel>Priority</FieldLabel>
          <TextField
            className="bg-white"
            type="number"
            value={String(draft.priority)}
            onChange={(e) => update({ priority: Number(e.target.value) })}
          />
        </div>
      </Section>

      <Section title="Eligibility">
        <EligibilityBuilder
          rules={draft.eligibility}
          onChange={(eligibility) => update({ eligibility })}
        />
        {windowSyncBlocked && (
          <Alert color="red" className="mt-3">
            Window sync steps can&apos;t be SUBSEQUENT-only. Add FULL to a rule&apos;s sync types,
            or remove the sync-type restriction.
          </Alert>
        )}
      </Section>

      <Section title="Metadata">
        <MetadataEditor metadata={draft.metadata} onChange={(metadata) => update({ metadata })} />
      </Section>
    </ConductorDrawer>
  );
};

export default StepEditorDrawer;
